import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { US_STATES } from "@/lib/us-states";
import { toast } from "sonner";
import { Trash2, ShieldAlert, ShoppingBag, Sparkles, UserPlus } from "lucide-react";

const TERMS_VERSION = "2026.02-rev2";

export default function Checkout() {
  const { user, cart, register, addToCart, removeFromCart, updateQty, clearCart } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Guest signup fields — only used when there is no active session. We create
  // the researcher account inline so buyers never hit a separate sign-up wall
  // yet we still capture the full shipping + contact profile compliance needs.
  const [guest, setGuest] = useState({ email: "", password: "", passwordConfirm: "", name: "", org: "" });
  const [shipping, setShipping] = useState({
    name: user?.name || "",
    org: user?.organization || "",
    line1: "", line2: "", city: "", state: "", zip: "", country: "US",
    phone: "",
    type: "commercial",
  });
  const [buyerType, setBuyerType] = useState(user?.buyer_type || "Research institution");
  const [att, setAtt] = useState({ all: false });
  const [cardEnabled, setCardEnabled] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("crypto_btc");
  // Admin-controlled roster from /payments/methods. Falls back to a sensible
  // static list if the fetch fails so checkout never becomes empty.
  const [paymentMethods, setPaymentMethods] = useState([
    { code: "crypto_btc", label: "Bitcoin", sub: "BTC · Bitcoin mainnet" },
    { code: "crypto_usdc_sol", label: "USDC", sub: "Solana (SPL)" },
    { code: "crypto_usdt_sol", label: "USDT", sub: "Solana (SPL)" },
  ]);
  const [affCode, setAffCode] = useState(localStorage.getItem("nebula_ref_code") || "");
  const [affApplied, setAffApplied] = useState(null); // {code, discount_pct}
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_type, discount_value, discount_usd, stacks_with_affiliate}
  const [loyalty, setLoyalty] = useState(null);           // {balance, balance_usd, ...}
  const [pointsInput, setPointsInput] = useState("");     // string, points buyer wants to redeem
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);
  const [shipOptions, setShipOptions] = useState([]);
  const [shipOptionId, setShipOptionId] = useState("");

  // Fetch the active shipping options once at mount. Admin can add/remove
  // carriers or edit prices at /admin/shipping — buyers here always see the
  // current list, defaulting to the first (lowest sort_order) option.
  useEffect(() => {
    api.get("/shipping/options").then((r) => {
      const list = r.data || [];
      setShipOptions(list);
      if (list.length && !shipOptionId) setShipOptionId(list[0].id);
    }).catch(() => {});
  }, []);

  // Fetch saved addresses once the user is authenticated. First-time buyers get
  // a "New address" experience; returning buyers see their default pre-filled.
  useEffect(() => {
    if (!user || !user.id) return;
    api.get("/account/addresses").then((r) => {
      const list = r.data || [];
      setAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setSelectedAddrId(def.id);
        setShipping({
          name: def.name || "", org: def.org || "",
          line1: def.line1 || "", line2: def.line2 || "",
          city: def.city || "", state: def.state || "",
          zip: def.zip || "", country: def.country || "US",
          phone: def.phone || "",
          type: "commercial",
        });
      }
    }).catch(() => {});
  }, [user]);

  // Last-line-of-defense stock scrub. Buyer may have opened this tab hours
  // ago; re-check every line item against current inventory the moment the
  // checkout mounts (guest + authed both hit the same public endpoint).
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    api.post("/cart/scrub", { items: cart }).then(({ data }) => {
      const dropped = data?.dropped_items || [];
      if (dropped.length === 0) return;
      dropped.forEach((d) => removeFromCart(d.sku, d.variant));
      const list = dropped.map((d) => `${d.name || d.sku}${d.variant ? ` · ${d.variant}` : ""}`).join(", ");
      toast.error(`Removed from cart — out of stock: ${list}`);
    }).catch(() => {});
    // Intentionally run once per mount; buyer edits inside the page use the
    // live add/remove APIs that already sync back to the server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickAddress = (id) => {
    setSelectedAddrId(id);
    if (id === "__new__") {
      setShipping({
        name: user?.name || "", org: user?.organization || "",
        line1: "", line2: "", city: "", state: "", zip: "", country: "US",
        phone: "",
        type: "commercial",
      });
      return;
    }
    const a = addresses.find((x) => x.id === id);
    if (!a) return;
    setShipping({
      name: a.name || "", org: a.org || "",
      line1: a.line1 || "", line2: a.line2 || "",
      city: a.city || "", state: a.state || "",
      zip: a.zip || "", country: a.country || "US",
      phone: a.phone || "",
      type: "commercial",
    });
  };

  useEffect(() => {
    api.get("/payments/card/config").then((r) => {
      const enabled = !!r.data.enabled;
      setCardEnabled(enabled);
    }).catch(() => {});
    // Pull the admin-controlled payment method list. If the current pick
    // is no longer in the enabled list (admin just flipped it off), fall
    // back to the first available option so the UI never gets stuck.
    api.get("/payments/methods").then((r) => {
      const list = r.data?.methods || [];
      setPaymentMethods(list);
      setPaymentMethod((prev) => list.some((m) => m.code === prev) ? prev : (list[0]?.code || "crypto_btc"));
    }).catch(() => {});
  }, []);

  const applyAffCode = async (raw, silent = false) => {
    const code = (typeof raw === "string" ? raw : affCode).trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.post("/affiliate/validate", { code });
      if (data.valid) {
        setAffApplied({ code: data.code, discount_pct: data.discount_pct });
        toast.success(`Code ${data.code} applied — ${data.discount_pct}% off`);
      } else {
        setAffApplied(null);
        if (silent) localStorage.removeItem("nebula_ref_code");
        else toast.error("Invalid or inactive code.");
      }
    } catch {
      if (!silent) toast.error("Could not validate code.");
    }
  };

  const applyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    try {
      // Compute the "other discount" percent so welcome-style codes can enforce stacking rules.
      const subtotalNow = cart.reduce((a, b) => a + b.price * b.quantity, 0);
      const affPctNow = affApplied ? affApplied.discount_pct : 0;
      const { data } = await api.post("/promo/validate", {
        code, subtotal: subtotalNow, has_affiliate: !!affApplied, other_discount_pct: affPctNow,
      });
      if (data.valid) {
        setPromoApplied({
          code: data.code,
          discount_type: data.discount_type,
          discount_value: data.discount_value,
          discount_usd: data.discount_usd,
          stacks_with_affiliate: data.stacks_with_affiliate,
        });
        toast.success(`Promo ${data.code} applied`);
      } else {
        setPromoApplied(null);
        toast.error(data.reason || "Invalid promo code.");
      }
    } catch {
      toast.error("Could not validate promo code.");
    }
  };

  // Auto-apply a referral code captured from a ?ref= share link
  useEffect(() => {
    const saved = localStorage.getItem("nebula_ref_code");
    if (saved) applyAffCode(saved, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the buyer's loyalty balance so we can show the "Redeem points" widget.
  useEffect(() => {
    if (!user?.id) return;
    api.get("/account/loyalty").then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
  }, [user?.id]);

  const subtotal = cart.reduce((a, b) => a + b.price * b.quantity, 0);
  const affDiscount = affApplied ? Math.round(subtotal * affApplied.discount_pct) / 100 : 0;
  const promoDiscount = promoApplied
    ? promoApplied.discount_type === "fixed"
      ? Math.min(promoApplied.discount_value, subtotal)
      : Math.round(subtotal * promoApplied.discount_value) / 100
    : 0;
  const preLoyaltyDiscount = affDiscount + promoDiscount;
  const remainingForPoints = Math.max(0, subtotal - preLoyaltyDiscount);
  const requestedPoints = Math.max(0, parseInt(pointsInput, 10) || 0);
  const maxRedeemablePoints = Math.min(
    loyalty?.balance || 0,
    Math.floor(remainingForPoints * 100), // 100 pts = $1
  );
  const pointsToRedeem = Math.min(requestedPoints, maxRedeemablePoints);
  const loyaltyDiscount = pointsToRedeem / 100;
  const discount = Math.min(preLoyaltyDiscount + loyaltyDiscount, subtotal);
  const selectedShipOption = shipOptions.find((o) => o.id === shipOptionId) || shipOptions[0] || null;
  const rawShipFee = selectedShipOption ? Number(selectedShipOption.price_usd || 0) : 0;
  // Free shipping kicks in once subtotal reaches $199.99, matching the sitewide banner.
  const FREE_SHIPPING_THRESHOLD = 199.99;
  const shippingFee = subtotal === 0
    ? 0
    : (subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : rawShipFee);
  const total = subtotal - discount + shippingFee;

  const redeemMax = () => setPointsInput(String(maxRedeemablePoints));
  const clearRedeem = () => setPointsInput("");

  // If affiliate is applied and current promo doesn't stack, auto-remove it.
  useEffect(() => {
    if (affApplied && promoApplied && !promoApplied.stacks_with_affiliate) {
      setPromoApplied(null);
      toast.info(`Promo ${promoApplied.code} removed — it doesn't stack with affiliate codes.`);
    }
  }, [affApplied, promoApplied]);

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="h-12 w-12 mx-auto text-[var(--ink-muted)]" strokeWidth={1.4} />
        <h1 className="font-display text-3xl mt-4">Your bag is empty.</h1>
        <p className="text-sm text-[var(--ink-soft)] mt-2">Browse the catalog to add research compounds.</p>
        <Link to="/shop" className="btn-primary inline-flex mt-6" data-testid="empty-cart-cta">Shop the catalog</Link>
      </div>
    );
  }

  const allAtt = att.all;
  const phoneDigitCount = (shipping.phone || "").replace(/\D/g, "").length;
  const shippingOk = shipping.name && shipping.line1 && shipping.city && shipping.state && shipping.zip && phoneDigitCount >= 7;
  // Guest checkout requires enough profile info to open a researcher account
  // and to contact them about the order. Password lives here so we can hash
  // it in the exact same auth flow returning buyers use.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email.trim());
  const guestFieldsOk = user
    ? true
    : emailValid
      && guest.password.length >= 8
      && guest.password === guest.passwordConfirm
      && !!guest.name.trim();
  const formOk = shippingOk && guestFieldsOk;

  const submit = async () => {
    if (!allAtt) { toast.error("Please accept the researcher agreement."); return; }
    if (!shippingOk) { toast.error("Complete the shipping address."); return; }
    if (!guestFieldsOk) { toast.error("Complete your account details (email, password, name)."); return; }
    setBusy(true);
    try {
      // Guest → create researcher account inline. Once this succeeds the
      // AuthContext sets cookies + Bearer token, so the /orders call below
      // is authenticated exactly like a returning buyer.
      if (!user) {
        const reg = await register({
          email: guest.email.trim().toLowerCase(),
          password: guest.password,
          name: guest.name.trim(),
          organization: guest.org.trim() || undefined,
          buyer_type: buyerType,
        });
        if (!reg.ok) {
          const msg = (reg.error || "").toLowerCase().includes("already")
            ? "That email already has an account. Please sign in first."
            : (reg.error || "Could not create your researcher account.");
          toast.error(msg);
          setBusy(false);
          return;
        }
      }
      // Every prior attestation flag is preserved server-side by re-expanding the single
      // consolidated toggle. Legal terms + audit surface unchanged.
      const expandedAtt = {
        age_21: true, ruo: true, no_human: true, no_veterinary: true, no_resale_human: true, terms: true,
        combined: true,
      };
      const { data } = await api.post("/orders", {
        items: cart.map((i) => ({ sku: i.sku, variant: i.variant, quantity: i.quantity })),
        shipping_address: shipping,
        buyer_type: buyerType,
        attestations: expandedAtt,
        terms_version: TERMS_VERSION,
        payment_method: paymentMethod,
        affiliate_code: affApplied?.code || undefined,
        promo_code: promoApplied?.code || undefined,
        loyalty_points_to_redeem: pointsToRedeem || 0,
        shipping_option_id: shipOptionId || undefined,
      });
      // Optionally save the shipping address for future orders (fire-and-forget).
      if (saveAddress && (selectedAddrId === "__new__" || addresses.length === 0) && addresses.length < 5) {
        api.post("/account/addresses", {
          label: shipping.org || shipping.city || "Shipping",
          name: shipping.name, org: shipping.org, line1: shipping.line1, line2: shipping.line2,
          city: shipping.city, state: shipping.state, zip: shipping.zip, country: shipping.country,
          phone: shipping.phone,
          is_default: addresses.length === 0,
        }).catch(() => {});
      }
      clearCart();
      toast.success(`Order ${data.order_number} placed`);
      if (paymentMethod.startsWith("crypto_") || paymentMethod === "card") {
        navigate(`/pay/${data.order_number}`);
      } else {
        navigate(`/order/${data.order_number}`, { state: { order: data } });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Order failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="eyebrow">Checkout</div>
      <h1 className="h-display text-4xl md:text-5xl mt-2">Your order.</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          {/* Guest account creation — required for guest checkout. Signed-in
              buyers skip this section entirely. */}
          {!user && (
            <section data-testid="guest-signup-section">
              <div className="eyebrow mb-3 flex items-center gap-2"><UserPlus className="h-3.5 w-3.5" /> Your researcher account</div>
              <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-5 space-y-4">
                <p className="text-[13px] text-[var(--ink-soft)] leading-relaxed">
                  Orders require a researcher account so you can track shipments, download invoices, and
                  view your COAs. We'll create one for you now — no separate sign-up wall.
                  Already registered? <Link to="/login" className="underline underline-offset-2 hover:text-[var(--terra)]">Sign in first</Link>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="Business email"
                    v={guest.email}
                    on={(v) => setGuest({ ...guest, email: v })}
                    test="guest-email"
                    placeholder="you@lab.example"
                    full
                  />
                  <Field
                    label="Full name"
                    v={guest.name}
                    on={(v) => setGuest({ ...guest, name: v })}
                    test="guest-name"
                  />
                  <Field
                    label="Organization / lab (optional)"
                    v={guest.org}
                    on={(v) => setGuest({ ...guest, org: v })}
                    test="guest-org"
                  />
                  <div>
                    <label className="eyebrow block mb-1.5">Password (min 8 chars)</label>
                    <input
                      type="password"
                      value={guest.password}
                      onChange={(e) => setGuest({ ...guest, password: e.target.value })}
                      className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                      data-testid="guest-password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-1.5">Confirm password</label>
                    <input
                      type="password"
                      value={guest.passwordConfirm}
                      onChange={(e) => setGuest({ ...guest, passwordConfirm: e.target.value })}
                      className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                      data-testid="guest-password-confirm"
                      autoComplete="new-password"
                    />
                    {guest.password && guest.passwordConfirm && guest.password !== guest.passwordConfirm && (
                      <div className="mt-1 text-[11px] text-[var(--crimson)] font-mono" data-testid="guest-password-mismatch">Passwords don't match.</div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Cart */}
          <section>
            <div className="eyebrow mb-3">Bag · {cart.length} item{cart.length !== 1 ? "s" : ""}</div>
            <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] divide-y divide-[var(--line)]">
              {cart.map((i) => (
                <div key={`${i.sku}-${i.variant}`} className="flex items-center gap-4 p-4" data-testid={`cart-row-${i.sku}`}>
                  <div className="w-16 h-16 bg-[var(--paper-2)] rounded-md overflow-hidden shrink-0">
                    {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{i.name}</div>
                    <div className="font-mono text-[11px] text-[var(--ink-muted)] mt-0.5">{i.sku} · {i.variant}</div>
                  </div>
                  <div className="inline-flex items-center border border-[var(--line)] rounded-full overflow-hidden">
                    <button onClick={() => updateQty(i.sku, i.variant, i.quantity - 1)} className="px-2.5 py-1 hover:bg-[var(--paper-2)]">−</button>
                    <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${i.sku}`}>{i.quantity}</span>
                    <button
                      onClick={() => {
                        const cap = Number(i.max_per_order || 0);
                        const skuTotal = cart.filter((x) => x.sku === i.sku).reduce((s, x) => s + x.quantity, 0);
                        if (cap > 0 && skuTotal >= cap) {
                          toast.error(`${i.name} is limited to ${cap} per order.`);
                          return;
                        }
                        updateQty(i.sku, i.variant, i.quantity + 1);
                      }}
                      className="px-2.5 py-1 hover:bg-[var(--paper-2)]"
                      data-testid={`cart-inc-${i.sku}`}
                    >+</button>
                  </div>
                  <div className="font-display font-medium w-20 text-right">${(i.price * i.quantity).toFixed(2)}</div>
                  <button onClick={() => removeFromCart(i.sku, i.variant)} className="text-[var(--ink-muted)] hover:text-[var(--crimson)]" aria-label="Remove" data-testid={`cart-remove-${i.sku}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <CompanionSuggestions cart={cart} addToCart={addToCart} />
          </section>

          {/* Shipping */}
          <section>
            <div className="eyebrow mb-3">Shipping</div>
            {addresses.length > 0 && (
              <div className="mb-4">
                <label className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-muted)] block mb-1.5">Ship to</label>
                <select
                  value={selectedAddrId}
                  onChange={(e) => pickAddress(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md"
                  data-testid="ship-saved-select"
                >
                  {addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.label || "Address")}{a.is_default ? " (default)" : ""} · {a.name} · {a.city}, {a.state} {a.zip}
                    </option>
                  ))}
                  <option value="__new__">+ Ship to a new address</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Recipient name" v={shipping.name} on={(v) => setShipping({ ...shipping, name: v })} test="ship-name" />
              <Field label="Organization" v={shipping.org} on={(v) => setShipping({ ...shipping, org: v })} test="ship-org" />
              <Field label="Address line 1" v={shipping.line1} on={(v) => setShipping({ ...shipping, line1: v })} test="ship-line1" full />
              <Field label="Address line 2" v={shipping.line2} on={(v) => setShipping({ ...shipping, line2: v })} test="ship-line2" full />
              <Field label="City" v={shipping.city} on={(v) => setShipping({ ...shipping, city: v })} test="ship-city" />
              <div>
                <label className="eyebrow block mb-1.5">State</label>
                <select
                  value={shipping.state}
                  onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                  data-testid="ship-state"
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </div>
              <Field label="ZIP" v={shipping.zip} on={(v) => setShipping({ ...shipping, zip: v })} test="ship-zip" />
              <Field
                label="Phone (required · for carrier delivery)"
                v={shipping.phone}
                on={(v) => setShipping({ ...shipping, phone: v })}
                test="ship-phone"
                placeholder="+1 555 123 4567"
              />
              <div>
                <label className="eyebrow block mb-1.5">Address type</label>
                <select value={shipping.type} onChange={(e) => setShipping({ ...shipping, type: e.target.value })} className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md" data-testid="ship-type">
                  <option value="commercial">Commercial / Lab</option>
                  <option value="residential">Residential</option>
                </select>
              </div>
              {(selectedAddrId === "__new__" || addresses.length === 0) && addresses.length < 5 && (
                <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer text-[var(--ink-soft)]" data-testid="save-address-row">
                  <input
                    type="checkbox"
                    checked={saveAddress}
                    onChange={(e) => setSaveAddress(e.target.checked)}
                    className="h-4 w-4 accent-[var(--terra)]"
                    data-testid="save-address-checkbox"
                  />
                  <span>Save this address to my account for future orders</span>
                </label>
              )}
              <div className="md:col-span-2">
                <label className="eyebrow block mb-1.5">Buyer type</label>
                <select value={buyerType} onChange={(e) => setBuyerType(e.target.value)} className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md" data-testid="ship-buyer-type">
                  {["Laboratory", "Research institution", "Educational institution", "Chemical reseller", "Licensed business buyer", "Other approved business use"].map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Shipping method */}
          <section data-testid="ship-method-section">
            <div className="eyebrow mb-3">Shipping method</div>
            {shipOptions.length === 0 ? (
              <div className="text-sm text-[var(--ink-soft)]">Shipping is currently unavailable. Please contact support.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ship-options">
                {shipOptions.map((o) => {
                  const active = o.id === shipOptionId;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setShipOptionId(o.id)}
                      className={`text-left border rounded-md px-4 py-3 transition-colors ${active ? "border-[var(--terra)] bg-[var(--terra)]/10" : "border-[var(--line)] bg-[var(--paper-2)] hover:border-[var(--terra)]/60"}`}
                      data-testid={`ship-option-${o.code}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[14px]">{o.carrier} · {o.name}</div>
                          <div className="text-[11.5px] text-[var(--ink-soft)] mt-0.5">{o.delivery_days || "Estimated 2 business days"}</div>
                        </div>
                        <div className="font-mono font-semibold text-[var(--terra)] whitespace-nowrap">${Number(o.price_usd).toFixed(2)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Payment method */}
          <section>
            <div className="eyebrow mb-3">Payment method</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payment-methods-grid">
              {paymentMethods.length === 0 ? (
                <div className="col-span-full border border-[var(--crimson)]/40 bg-[var(--crimson)]/10 rounded-md px-3 py-3 text-sm text-[var(--crimson)]" data-testid="no-payment-methods">
                  No payment methods are currently available. Please contact support@nebulaaminos.com to place your order.
                </div>
              ) : (
                paymentMethods.map((m) => (
                  <PMOption
                    key={m.code}
                    value={m.code}
                    current={paymentMethod}
                    onPick={setPaymentMethod}
                    title={m.label}
                    sub={m.code === "card" && !cardEnabled ? "Coming soon · waitlist" : m.sub}
                    testid={`pm-${m.code.replace(/_/g, "-")}`}
                    disabled={m.code === "card" && !cardEnabled}
                  />
                ))
              )}
            </div>
          </section>

          {/* Attestation — consolidated researcher agreement */}
          <section>
            <div className="eyebrow mb-3">Researcher agreement</div>
            <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-5">
              <div className="flex items-start gap-3 pb-4 border-b border-[var(--line)]">
                <ShieldAlert className="h-5 w-5 text-[var(--terra)] shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed text-[var(--ink-soft)]">
                  By placing this order I confirm that I am acquiring these materials for laboratory research only. I will not use them for human or veterinary administration, diagnosis, treatment, prevention, food, cosmetic, or household purposes.
                </p>
              </div>
              <label
                className="flex items-start gap-3 pt-4 cursor-pointer group"
                data-testid="attest-all-row"
              >
                <input
                  type="checkbox"
                  checked={att.all}
                  onChange={(e) => setAtt({ all: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-[var(--terra)] cursor-pointer"
                  data-testid="attest-all"
                />
                <span className="text-sm leading-relaxed text-[var(--ink)] group-hover:text-[var(--terra)] transition-colors">
                  I certify that I am <span className="font-semibold">21 or older</span>, that I am purchasing these products for <span className="font-semibold">laboratory research use only</span>, and that I will not use them for human or animal consumption. I have read and agree to the <a href="/compliance" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[var(--terra)]">Research Use Only disclaimer</a>, <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[var(--terra)]">Terms &amp; Conditions</a>, and <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[var(--terra)]">Refund Policy</a>. <span className="font-mono text-[10.5px] text-[var(--ink-muted)]">({TERMS_VERSION})</span>
                </span>
              </label>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] sticky top-28">
            <div className="px-5 py-4 border-b border-[var(--line)] bg-[var(--paper-2)] rounded-t-md">
              <div className="eyebrow">Summary</div>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm">
              {affApplied ? (
                <div className="flex items-center justify-between pb-2 border-b border-[var(--line)]" data-testid="affiliate-applied">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--olive)]">Code {affApplied.code} · {affApplied.discount_pct}% off</span>
                  <button type="button" onClick={() => setAffApplied(null)} className="text-[11px] text-[var(--crimson)] hover:underline" data-testid="affiliate-remove">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2 pb-2 border-b border-[var(--line)]">
                  <input
                    value={affCode}
                    onChange={(e) => setAffCode(e.target.value.toUpperCase())}
                    placeholder="Referral / affiliate code"
                    className="flex-1 min-w-0 px-3 py-2 border border-[var(--line)] bg-[var(--paper)] font-mono text-xs rounded-md outline-none focus:border-[var(--terra)]"
                    data-testid="affiliate-code-input"
                  />
                  <button type="button" onClick={applyAffCode} className="px-3 py-2 border border-[var(--line)] rounded-md text-[11px] font-mono uppercase tracking-wider hover:border-[var(--terra)]" data-testid="affiliate-apply">
                    Apply
                  </button>
                </div>
              )}
              {promoApplied ? (
                <div className="flex items-center justify-between pb-2 border-b border-[var(--line)]" data-testid="promo-applied">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--olive)]">
                    Promo {promoApplied.code} · {promoApplied.discount_type === "fixed" ? `$${promoApplied.discount_value.toFixed(2)}` : `${promoApplied.discount_value}%`} off
                  </span>
                  <button type="button" onClick={() => setPromoApplied(null)} className="text-[11px] text-[var(--crimson)] hover:underline" data-testid="promo-remove">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2 pb-2 border-b border-[var(--line)]">
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Discount / promo code"
                    className="flex-1 min-w-0 px-3 py-2 border border-[var(--line)] bg-[var(--paper)] font-mono text-xs rounded-md outline-none focus:border-[var(--terra)]"
                    data-testid="promo-code-input"
                  />
                  <button type="button" onClick={applyPromoCode} className="px-3 py-2 border border-[var(--line)] rounded-md text-[11px] font-mono uppercase tracking-wider hover:border-[var(--terra)]" data-testid="promo-apply">
                    Apply
                  </button>
                </div>
              )}
              {loyalty && loyalty.balance > 0 && (
                <div className="pb-2 border-b border-[var(--line)]" data-testid="loyalty-widget">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[var(--terra)]">
                    <Sparkles className="h-3.5 w-3.5" /> Loyalty points
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--ink-muted)]">
                    You have <span className="font-mono text-[var(--ink)]">{loyalty.balance} pts</span> (${loyalty.balance_usd.toFixed(2)} available). 100 pts = $1.
                    {loyalty.expiring_soon_pts > 0 && (
                      <span className="text-[var(--crimson)]"> · {loyalty.expiring_soon_pts} pts expire in the next 30 days</span>
                    )}
                  </div>
                  {pointsToRedeem > 0 ? (
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[11px] font-mono text-[var(--olive)]">
                        Redeeming {pointsToRedeem} pts (−${loyaltyDiscount.toFixed(2)})
                      </span>
                      <button type="button" onClick={clearRedeem} className="text-[11px] text-[var(--crimson)] hover:underline" data-testid="loyalty-clear">Remove</button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <input
                        value={pointsInput}
                        onChange={(e) => setPointsInput(e.target.value.replace(/\D/g, ""))}
                        placeholder={`Points to redeem (max ${maxRedeemablePoints})`}
                        inputMode="numeric"
                        className="flex-1 min-w-0 px-3 py-2 border border-[var(--line)] bg-[var(--paper)] font-mono text-xs rounded-md outline-none focus:border-[var(--terra)]"
                        data-testid="loyalty-input"
                      />
                      <button
                        type="button"
                        onClick={redeemMax}
                        disabled={maxRedeemablePoints === 0}
                        className="px-3 py-2 border border-[var(--line)] rounded-md text-[11px] font-mono uppercase tracking-wider hover:border-[var(--terra)] disabled:opacity-40"
                        data-testid="loyalty-max"
                      >
                        Redeem max
                      </button>
                    </div>
                  )}
                </div>
              )}
              <Row k="Subtotal" v={`$${subtotal.toFixed(2)}`} />
              {affDiscount > 0 && <Row k={`Affiliate (${affApplied.code})`} v={`−$${affDiscount.toFixed(2)}`} muted />}
              {promoDiscount > 0 && <Row k={`Promo (${promoApplied.code})`} v={`−$${promoDiscount.toFixed(2)}`} muted />}
              {loyaltyDiscount > 0 && <Row k={`Points (${pointsToRedeem})`} v={`−$${loyaltyDiscount.toFixed(2)}`} muted />}
              <Row
                k={selectedShipOption ? `Shipping · ${selectedShipOption.carrier}` : "Shipping"}
                v={shippingFee === 0 ? "FREE" : `$${shippingFee.toFixed(2)}`}
                muted={shippingFee === 0}
              />
              <div className="border-t border-[var(--line)] my-1"></div>
            </div>
            <div className="px-5 py-4 border-t border-[var(--line)] flex items-baseline justify-between">
              <div className="eyebrow">Total</div>
              <div className="font-display text-3xl font-semibold">${total.toFixed(2)}</div>
            </div>
            <div className="px-5 pb-5">
              {(!allAtt || !formOk) && !busy && (
                <div
                  className="mb-3 border border-[var(--crimson)]/50 bg-[var(--crimson)]/10 rounded-md px-3 py-2.5 text-[12.5px] text-[var(--crimson)] leading-snug"
                  data-testid="place-order-blocker"
                >
                  <div className="font-mono uppercase tracking-[0.14em] text-[10.5px] font-semibold mb-1">Before you continue</div>
                  <ul className="space-y-0.5">
                    {!user && !guestFieldsOk && (
                      <li data-testid="blocker-account">
                        <button
                          type="button"
                          onClick={() => document.querySelector('[data-testid="guest-signup-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
                          className="underline underline-offset-2 hover:opacity-80"
                        >
                          Complete your researcher account (email, name, and matching passwords of 8+ chars).
                        </button>
                      </li>
                    )}
                    {!shippingOk && (
                      <li data-testid="blocker-address">
                        <button
                          type="button"
                          onClick={() => document.querySelector('[data-testid="ship-state"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
                          className="underline underline-offset-2 hover:opacity-80"
                        >
                          Complete the shipping address (name, street, city, state, ZIP, and a 7+ digit phone).
                        </button>
                      </li>
                    )}
                    {!allAtt && (
                      <li data-testid="blocker-agreement">
                        <button
                          type="button"
                          onClick={() => document.querySelector('[data-testid="attest-all-row"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
                          className="underline underline-offset-2 hover:opacity-80"
                        >
                          Check the &ldquo;I confirm I am 21+ … research use only&rdquo; box below.
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
              <button disabled={busy || !allAtt || !formOk} onClick={submit} className="btn-primary w-full justify-center disabled:bg-[var(--ink-muted)] disabled:cursor-not-allowed" data-testid="place-order">
                {busy ? "Submitting…" : paymentMethod === "card" ? "Continue to card" : paymentMethod.startsWith("crypto_") ? "Continue to payment" : "Place order"}
              </button>
              <p className="text-[11px] font-mono text-[var(--ink-muted)] mt-3 leading-relaxed">
                {paymentMethod.startsWith("crypto_")
                  ? "Stock is reserved for 24 hours. Your order is fulfilled after the on-chain payment is confirmed."
                  : paymentMethod === "card"
                  ? cardEnabled
                    ? "You'll be redirected to VERIFIED Crypto Checkout's secure hosted page to pay by card. Stock is reserved for 24 hours."
                    : "Card processing is being onboarded — you'll be added to the waitlist and notified the moment it goes live."
                  : "Orders enter a brief compliance review queue before release. No payment is captured at this stage."}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, v, on, test, full, placeholder }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="eyebrow block mb-1.5">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]" data-testid={test} />
    </div>
  );
}
function Row({ k, v, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--ink-muted)]">{k}</span>
      <span className={muted ? "text-[var(--olive)] font-mono text-xs uppercase tracking-wider font-semibold" : "font-mono"}>{v}</span>
    </div>
  );
}
function PMOption({ value, current, onPick, title, sub, testid, disabled }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      data-testid={testid}
      className={`text-left p-4 border rounded-md transition-colors ${
        active
          ? "border-[var(--terra)] bg-[var(--terra)]/10"
          : "border-[var(--line)] bg-[var(--paper-2)] hover:border-[var(--terra)]/40"
      } ${disabled ? "opacity-90" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">{title}</div>
        <div className={`h-4 w-4 rounded-full border ${active ? "bg-[var(--terra)] border-[var(--terra)]" : "border-[var(--line)]"}`} />
      </div>
      <div className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)]">{sub}</div>
    </button>
  );
}


// Companion-product suggestions rendered under the cart list.
// Every peptide order needs bacteriostatic water to reconstitute the
// lyophilized powder — surface BAC + Hospira BAC as one-click add-ons unless
// one of them is already in the cart.
function CompanionSuggestions({ cart, addToCart }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const cartSkus = new Set(cart.map((c) => c.sku));
  const alreadyHasBac = cartSkus.has("BAC") || cartSkus.has("NA-BACWATER");
  // Any non-BAC item in the cart is a candidate for reconstitution — since
  // this is a research-peptide catalog, the companion suggestion applies broadly.
  const hasReconstitutable = cart.some((i) => !["BAC", "NA-BACWATER"].includes(i.sku));

  if (cart.length === 0 || !hasReconstitutable || alreadyHasBac) return null;

  const companions = products
    .filter((p) => ["BAC", "NA-BACWATER"].includes(p.sku))
    .filter((p) => p.any_in_stock !== false);
  if (companions.length === 0) return null;

  const onAdd = (p) => {
    const v = (p.variants || [])[0] || {};
    addToCart({
      sku: p.sku,
      name: p.product_name,
      variant: v.name || p.pack_size || "default",
      price: v.price || p.price_usd,
      image: p.image_url || v.image_url || "",
      category_slug: p.category_slug,
      max_per_order: p.max_per_order,
      quantity: 1,
    });
    toast.success(`${p.product_name} added — you'll need it to reconstitute the compound.`);
  };

  return (
    <div className="mt-4 border border-[var(--terra)]/40 bg-[var(--terra)]/5 rounded-md p-4" data-testid="companion-suggestions">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="eyebrow text-[var(--terra)]">Recommended companion</div>
          <div className="text-sm text-[var(--ink)] mt-1">You&apos;ll need bacteriostatic water to reconstitute the powder.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {companions.map((p) => {
          const v = (p.variants || [])[0] || {};
          return (
            <div key={p.sku} className="flex items-center gap-3 p-3 border border-[var(--line)] bg-[var(--paper)] rounded-md" data-testid={`companion-${p.sku}`}>
              <div className="w-14 h-14 bg-[var(--paper-2)] rounded shrink-0 overflow-hidden">
                {p.image_url && <img src={p.image_url} alt={p.product_name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{p.product_name}</div>
                <div className="font-mono text-[11px] text-[var(--ink-muted)] mt-0.5">
                  {v.name || p.pack_size || ""} · ${Number(v.price || p.price_usd || 0).toFixed(2)}
                </div>
              </div>
              <button
                onClick={() => onAdd(p)}
                className="text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 border border-[var(--terra)] text-[var(--terra)] rounded-full hover:bg-[var(--terra)] hover:text-white transition-colors"
                data-testid={`add-companion-${p.sku}`}
              >
                + Add
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
